import axios from "axios";
import router from "@/router";
import store from "@/store";
import Swal from "sweetalert2";
import { applyAuthTokensFromResponse, isCookieRefreshEnabled } from "@/utils/auth-tokens";
import { isAnotherTabRefreshing, waitForRefreshLockToClear } from "@/utils/tokenRefreshLock";
import { handleRefreshFailure, isAuthRequest, isLicenseActiveError, isTokenRefreshRequest, refreshAccessToken } from "@/utils/auth-service";

var config = {
  baseUrl: process.env.VUE_APP_API_URL,
};
// 디버깅
// if (process.env.NODE_ENV === "localhost") {
//   config.baseUrl = 'https://tortue.cogcom.kr:8000/api/v0/'
// }

const instance = axios.create({
  baseURL: config.baseUrl,
  withCredentials: true,
});

function isFormDataPayload(data) {
  return typeof FormData !== "undefined" && data instanceof FormData;
}

function removeContentTypeHeader(headers) {
  if (!headers) return;

  if (typeof headers.delete === "function") {
    headers.delete("Content-Type");
    headers.delete("Content-type");
    headers.delete("content-type");
    return;
  }

  delete headers["Content-Type"];
  delete headers["Content-type"];
  delete headers["content-type"];
}

function getResponseMeta(response) {
  if (!response) return {}
  return {
    method: response.config && response.config.method,
    url: response.config && response.config.url,
    status: response.status,
  }
}

function getRequestMeta(config) {
  if (!config) return {}
  return {
    method: config.method,
    url: config.url,
  }
}

function waitForMethodRecallToFinish(timeout = 10000) {
  return new Promise((resolve) => {
    const startedAt = Date.now();

    const check = () => {
      if (!store.state.isMethodRecalling || Date.now() - startedAt >= timeout) {
        resolve();
        return;
      }

      setTimeout(check, 50);
    };

    check();
  });
}

async function waitForActiveTokenRefresh(config) {
  if (config.skipAuth || isTokenRefreshRequest(config.url)) return;

  if (store.state.isMethodRecalling) {
    await waitForMethodRecallToFinish();
  }

  if (isAnotherTabRefreshing()) {
    await waitForRefreshLockToClear();
  }
}

function isProtectedRequest(config) {
  return !config.skipAuth && !isTokenRefreshRequest(config.url) && !isAuthRequest(config.url);
}

function shouldRefreshAccessTokenBeforeRequest(config) {
  return (
    isProtectedRequest(config) &&
    !store.state.access_token &&
    isCookieRefreshEnabled(store.state.auth_refresh_mode) &&
    Object.keys(store.state.user_info || {}).length > 0
  );
}

/*
  1. 요청 인터셉터를 작성합니다. 2개의 콜백 함수를 받습니다.

  1) 요청 바로 직전 - 인자값: axios config
  2) 요청 에러 - 인자값: error
*/
instance.interceptors.request.use(
  async function (config) {
    // 요청 바로 직전
    // axios 설정값에 대해 작성합니다.
    await waitForActiveTokenRefresh(config);

    config.headers = config.headers || {};
    if (isFormDataPayload(config.data)) {
      removeContentTypeHeader(config.headers);
    }
    config._skipAuth = config.skipAuth;
    if (config.headers.Authorization === "Bearer ") {
      delete config.headers.Authorization;
    }

    if (shouldRefreshAccessTokenBeforeRequest(config)) {
      try {
        await refreshAccessToken();
      } catch (refreshError) {
        handleRefreshFailure();
        return Promise.reject(refreshError);
      }
    }

    if (isProtectedRequest(config) && store.state.access_token) {
      config.headers.Authorization = `Bearer ${store.state.access_token}`;
    }
    delete config.skipAuth;
    console.log("interceptors_request_success", getRequestMeta(config));
    return config;
  },
  async function (error) {
    // 요청 에러 처리를 작성합니다.
    console.log("interceptors_request_error");
    return Promise.reject(error);
  }
);

/*
  2. 응답 인터셉터를 작성합니다. 2개의 콜백 함수를 받습니다.

  1) 응답 정성 - 인자값: http response
  2) 응답 에러 - 인자값: http error
*/
instance.interceptors.response.use(
  function (response) {
    /*
      http status가 200인 경우 응답 바로 직전에 대해 작성합니다. 
      .then() 으로 이어집니다.
    */
    console.log("interceptors_response_success", getResponseMeta(response));
    applyAuthTokensFromResponse(store, response.data);
    store.commit('setOverlay', false)
    store.commit('setOverlayText', '')
    return response;
  },

  async function (error) {
    /*
      http status가 200이 아닌 경우 응답 에러 처리를 작성합니다.
      .catch() 으로 이어집니다.    
    */
    console.log("interceptors_response_error", {
      ...getRequestMeta(error && error.config),
      status: error && error.response && error.response.status,
    });
    const originalConfig = error.config || {};
    const suppressErrorToastCodes = originalConfig.suppressGlobalErrorToastForErrCodes || [];
    const responseErrorCode = error.response && error.response.data && error.response.data.err_code;
    const suppressGlobalErrorToast = !!originalConfig.suppressGlobalErrorToast || suppressErrorToastCodes.includes(responseErrorCode);

    if (
      error.response &&
      error.response.status === 401 &&
      !originalConfig._retry &&
      !originalConfig._skipAuth &&
      !isTokenRefreshRequest(originalConfig.url) &&
      !isAuthRequest(originalConfig.url)
    ) {
      try {
        originalConfig._retry = true;
        await refreshAccessToken();
        originalConfig.headers = originalConfig.headers || {};
        if (store.state.access_token) {
          originalConfig.headers.Authorization = `Bearer ${store.state.access_token}`;
        }
        return instance(originalConfig);
      } catch (refreshError) {
        handleRefreshFailure();
        return Promise.reject(refreshError);
      }
    }

    if (suppressGlobalErrorToast) {
      console.log("interceptors_response_error suppressGlobalErrorToast", getRequestMeta(originalConfig));
    }
    else if (!error.response || error.response === undefined || error.response === 'undefined') {
      store.commit('swalToast', {title: '서버 접속이 지연되고 있습니다. 나중에 다시 시도해주세요.', type: 'warning'})
    }
    else if (error.response.status === 400) {
      if (error.response.data.detail === "InvaildAccess") {
        store.commit('swalToast', {title: '권한이 없습니다.', type: 'warning'})
      }
      else if (error.response.data.detail === "FileNotExist") {
        store.commit('swalToast', {title: '존재하지 않는 파일입니다.', type: 'warning'})
      }
      else if (error.response.data.detail === "UnsupportedFile") {
        store.commit('swalToast', {title: '지원하지 않는 파일입니다.', type: 'warning'})
      }
      else if (error.response.data.detail === "UnsupportedFormat") {
        store.commit('swalToast', {title: '지원하지 않는 포맷입니다.', type: 'warning'})
      }
      else if (error.response.data.err_code === "1001004") {
        store.commit('swalToast', {title: '잘못된 인증코드입니다.', type: 'warning'})
      }
      else if (error.response.data.err_code === "1001058") {
        store.commit('swalToast', {title: '이메일 또는 비밀번호를 다시 확인해주세요.', type: 'warning'})
      }
      else if (error.response.data.err_code === "1001121") {
        store.commit('swalToast', {title: '로그인 시도 횟수가 너무 많습니다. 잠시 후 다시 시도해주세요.', type: 'warning'})
      }
      else if (error.response.data.err_code === "1001138" || error.response.data.err_code === "1001139") {
        store.commit('swalToast', {title: '비밀번호를 8자 이상으로 입력해주세요.', type: 'warning'})
      }
      else if (error.response.data.err_code === "1015008") {
        store.commit('swalToast', {title: '이미 등록된 사업자등록번호입니다.', type: 'warning'})
      }
      else {
        console.log('router', router)
        console.log('router.currentRoute.name', router.currentRoute.name)
        if (router.currentRoute.name !== 'LandingPage') {
          if (error.response.data.err_code) {
            store.commit('swalToast', {title: `일시적인 오류로 에러가 발생했습니다.(에러코드: ${error.response.data.err_code}) \n 잠시 후 다시 시도해 주세요.`, type: 'error'})
          } else {
            store.commit('swalToast', {title: `일시적인 오류로 에러가 발생했습니다. \n 잠시 후 다시 시도해 주세요.`, type: 'error'})
          }
        }
      }
    }
    else if (error.response.status === 401) {
      console.log("401 error");
    }
    else if (error.response.status === 403) {
      // 작업 관리 등 구조화된 API 에러(code 보유)는 각 화면에서 apiErrorMessage 토스트로 처리하므로
      // 계정 사용 기한 만료 dialog를 띄우지 않는다. (예: WORK_ITEM_ACCESS_DENIED)
      const apiErrorCode = error.response.data?.code || error.response.data?.error?.code
      if (!apiErrorCode) {
        store.state.dateExpirationDialog = true
      }
    }
    else if (error.response.status === 422) {
      // 
    }
    else if (error.response.status === 429) {
      if (isLicenseActiveError(error)) {
        store.commit('swalToast', {title: '다시 로그인해 주세요.(1001175)', type: 'warning'})
        store.commit('logout')
        router.push('/').catch(() => {})
      }
      // 무료체험 종료 7일 이후
      else if (error.response.data.err_code === '1001149') {
        store.state.expireDateEndDialog = true
      }
      else if (
        error.response.data.err_code === '1012017' || 
        error.response.data.err_code === '1012018' || 
        error.response.data.err_code === '1012019' ||
        error.response.data.err_code === '1002338' ||
        error.response.data.err_code === '1002339'
      ) {
        Swal.fire({
          icon: "info",
          html: `
            <div style="display: flex; flex-direction: column; align-items: center">
              <div style="font-size: 16px; font-weight: 600; line-height: 20px; color: #414458; margin-bottom: 12px">
                무료체험 중에는 다음과 같은 사항들이 제한됩니다.
              </div>
              <ul style="list-style: inside; width: 412px; text-align: start; margin-left: 12px">
                <li style="font-size: 14px; font-weight: 500; line-height: 20px; color: #414458">
                  파일 1개당 최대 2MB까지 업로드 가능합니다.
                </li>
                <li style="font-size: 14px; font-weight: 500; line-height: 20px; color: #414458">
                  전체 저장공간은 프로젝트당 50MB까지 사용 가능합니다. (휴지통 포함)
                </li>
                <li style="font-size: 14px; font-weight: 500; line-height: 20px; color: #414458">
                  프로젝트는 3개까지 생성 가능합니다. (휴지통 포함)
                </li>
              </ul>
            </div>
          `,
          confirmButtonText: "확인",
        })
      }
    }
    else if (error.response.status === 501) {
      console.log("501 error");
    }
    else {
      store.commit('swalToast', {title: '다시 시도해 주세요.', type: 'warning'})
    }
    if (!error.response || error.response.status !== 401) {
      store.commit('setOverlay', false)
      store.commit('setOverlayText', '')
    }
    return Promise.reject(error);
  }
);

// axios 객체 생성
export default instance;
